import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

function OffersList() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      const response = await axios.get("/api/offers");
      if (response.data.status === "success") {
        setOffers(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching offers:", error);
    } finally {
      setLoading(false);
    }
  };

  const [searchTerm, setSearchTerm] = useState("");

  const filteredOffers = offers.filter(offer => 
    offer.agentName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleLogout = () => {
    localStorage.removeItem("isAuthenticated");
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
          <div className="flex items-center gap-4 w-full md:w-auto">
            <h1 className="text-3xl font-bold text-gray-800">All Offers</h1>
            <div className="relative">
              <input 
                type="text"
                placeholder="Search by Agent..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none w-64"
              />
              <svg className="w-4 h-4 text-gray-400 absolute left-3 top-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate("/app")}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            >
              ← Back to Dashboard
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-white text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition"
            >
              Logout
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-10">Loading offers...</div>
        ) : filteredOffers.length === 0 ? (
          <div className="text-center py-10 text-gray-500">
            {offers.length === 0 ? "No offers found." : "No offers match your search."}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-left border-collapse">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Agent</th>
                  <th className="p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Show / Company</th>
                  <th className="p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Check-in / Out</th>
                  <th className="p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredOffers.map((offer) => {
                  // Safe Date Rendering Logic
                  let dateDisplay = "N/A";
                  if (typeof offer.dates === "object" && offer.dates !== null) {
                    // It's likely { from, to } or similar
                     dateDisplay = `From ${offer.dates.from || "?"} To ${offer.dates.to || "?"}`;
                     // Sometimes it might be our new string format if the backend didn't parse it? 
                     // Actually our frontend sends specific string "From ... To ..." effectively as a string, but the schema says Mixed.
                     // The err said: object with keys {from, to}. So let's handle that.
                     if (offer.dates.from && offer.dates.to) {
                        dateDisplay = `${offer.dates.from} → ${offer.dates.to}`;
                     }
                  } else {
                    dateDisplay = offer.dates;
                  }

                  return (
                    <tr 
                      key={offer._id} 
                      onClick={() => navigate("/app", { state: { editOffer: offer } })}
                      className="hover:bg-indigo-50 cursor-pointer transition"
                    >
                      <td className="p-4 text-sm text-gray-500 font-mono">
                        {new Date(offer.createdAt).toLocaleDateString()}
                      </td>
                      <td className="p-4">
                        <div className="font-medium text-gray-800">{offer.customerName}</div>
                        <div className="text-xs text-gray-500">{offer.customerEmail}</div>
                      </td>
                      <td className="p-4 text-sm font-semibold text-indigo-600">
                        {offer.agentName || "-"}
                      </td>
                      <td className="p-4 text-sm text-gray-600">
                        <div>{offer.showName || "-"}</div>
                        <div className="text-xs text-gray-400">{offer.companyName}</div>
                      </td>
                      <td className="p-4 text-sm text-gray-600">
                        {dateDisplay}
                      </td>
                      <td className="p-4 text-sm text-indigo-600 font-medium">
                        Edit & Resend →
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default OffersList;
